import type { PlayerId } from './roster';
import { PLAYER_IDS } from './roster';
import { isLocked } from './lock';
import { clearUnlockedSheet } from './resetSheet';
import type { Game, PickStore, WeekSheet, WeekState } from './types';
import { findTiebreakerGame } from './scoring';
import { mergeWeekGames } from './espnIntel';

export const ACTIVE_KEY = 'pickem:active-player';
const WEEK_PREFIX = 'pickem:week:';
const SEASON_KEY = 'pickem:season-weeks';

function emptySheet(playerId: PlayerId, weekKey: string): WeekSheet {
  return { playerId, weekKey, picks: {} };
}

function emptySheets(weekKey: string): Record<PlayerId, WeekSheet> {
  const sheets = {} as Record<PlayerId, WeekSheet>;
  for (const id of PLAYER_IDS) {
    sheets[id] = emptySheet(id, weekKey);
  }
  return sheets;
}

export function createLocalStore(
  initialWeekKey: string,
  dateRange: string,
  games: Game[],
): PickStore {
  let weekKey = initialWeekKey;
  let state: WeekState = loadWeek(weekKey, dateRange, games);
  let activePlayer = loadActivePlayer();
  const listeners = new Set<() => void>();

  function emit(): void {
    for (const fn of listeners) fn();
  }

  function persist(): void {
    localStorage.setItem(WEEK_PREFIX + weekKey, JSON.stringify(state));
    rememberWeek(weekKey);
  }

  return {
    async init() {
      persist();
    },

    getWeek() {
      return state;
    },

    setActivePlayer(playerId) {
      activePlayer = playerId;
      saveActivePlayer(playerId);
      emit();
    },

    getActivePlayer() {
      return activePlayer;
    },

    async setPick(gameId, teamId) {
      const game = state.games.find((g) => g.id === gameId);
      if (game && isLocked(game.kickoffUtc)) return;
      const sheet = state.sheets[activePlayer];
      sheet.picks[gameId] = teamId;
      persist();
      emit();
    },

    async setTiebreaker(value) {
      const tb = state.games.find((g) => g.id === state.tiebreakerGameId);
      if (tb && isLocked(tb.kickoffUtc)) return;
      const sheet = state.sheets[activePlayer];
      sheet.tiebreaker = value;
      persist();
      emit();
    },

    async resetActiveSheet() {
      state.sheets[activePlayer] = clearUnlockedSheet(
        state.sheets[activePlayer],
        state.games,
        state.tiebreakerGameId,
      );
      persist();
      emit();
    },

    async refreshGames(newGames) {
      const games = mergeWeekGames(state.games, newGames);
      state = {
        ...state,
        games,
        tiebreakerGameId: findTiebreakerGame(games)?.id ?? null,
        gamesSnapshotAt: new Date().toISOString(),
      };
      persist();
      emit();
    },

    async replaceWeek(next) {
      persist();
      weekKey = next.weekKey;
      state = loadWeek(next.weekKey, next.dateRange, next.games);
      persist();
      emit();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

function loadWeek(weekKey: string, dateRange: string, games: Game[]): WeekState {
  const raw = localStorage.getItem(WEEK_PREFIX + weekKey);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as WeekState;
      return {
        ...parsed,
        dateRange,
        games,
        sheets: { ...emptySheets(weekKey), ...parsed.sheets },
        tiebreakerGameId: findTiebreakerGame(games)?.id ?? null,
      };
    } catch {
      /* fall through */
    }
  }
  return {
    weekKey,
    dateRange,
    games,
    sheets: emptySheets(weekKey),
    tiebreakerGameId: findTiebreakerGame(games)?.id ?? null,
  };
}

export function loadActivePlayer(): PlayerId {
  const stored = localStorage.getItem(ACTIVE_KEY) as PlayerId | null;
  if (stored && PLAYER_IDS.includes(stored)) return stored;
  return 'dave';
}

export function saveActivePlayer(playerId: PlayerId): void {
  localStorage.setItem(ACTIVE_KEY, playerId);
}

function rememberWeek(weekKey: string): void {
  const raw = localStorage.getItem(SEASON_KEY);
  const weeks = raw ? (JSON.parse(raw) as string[]) : [];
  if (!weeks.includes(weekKey)) {
    weeks.push(weekKey);
    localStorage.setItem(SEASON_KEY, JSON.stringify(weeks));
  }
}

export function listSeasonWeekKeys(): string[] {
  const raw = localStorage.getItem(SEASON_KEY);
  return raw ? (JSON.parse(raw) as string[]) : [];
}

export function loadWeekState(weekKey: string): WeekState | null {
  const raw = localStorage.getItem(WEEK_PREFIX + weekKey);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as WeekState;
  } catch {
    return null;
  }
}
